import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  segments: DonutSegment[];
  size?: number;
  /** Shown in the donut's center — typically the sum of all segment values. */
  centerLabel?: string;
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

function arcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

/** Hand-rolled SVG donut chart — arcs built via standard SVG M/A path math
 * (no charting library available in this codebase). A 2px surface-color gap
 * is left between adjacent segments (stroke matching the card background) so
 * segments read as visually distinct instead of one continuous ring, and a
 * plain-View legend renders below since color alone should never carry the
 * only distinguishing signal. */
export const DonutChart: React.FC<DonutChartProps> = ({ segments, size = 160, centerLabel }) => {
  const styles = useThemedStyles(getStyles);
  const strokeWidth = size * 0.22;
  const radius = (size - strokeWidth) / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0);

  const arcs = useMemo(() => {
    if (total === 0) return [];
    let cumulativeAngle = 0;
    return segments
      .filter((s) => s.value > 0)
      .map((s) => {
        const angle = (s.value / total) * 360;
        const startAngle = cumulativeAngle;
        const endAngle = cumulativeAngle + Math.max(angle - (segments.length > 1 ? 2 : 0), 1);
        cumulativeAngle += angle;
        return { ...s, path: arcPath(size / 2, size / 2, radius, startAngle, endAngle) };
      });
  }, [segments, total, size, radius]);

  return (
    <View style={styles.row}>
      <View style={{ width: size, height: size }}>
        <Svg width={size} height={size}>
          {total === 0 ? (
            <Circle cx={size / 2} cy={size / 2} r={radius} stroke={colors.border as string} strokeWidth={strokeWidth} fill="none" />
          ) : (
            arcs.map((a) => (
              <Path key={a.label} d={a.path} stroke={a.color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" />
            ))
          )}
        </Svg>
        <View style={[StyleSheet.absoluteFill, styles.centerLabelWrap]} pointerEvents="none">
          <Text style={styles.centerLabel} numberOfLines={1}>{centerLabel ?? String(total)}</Text>
        </View>
      </View>
      <View style={styles.legend}>
        {segments.map((s) => (
          <View key={s.label} style={styles.legendRow}>
            <View style={styles.legendLabelGroup}>
              <View style={[styles.swatch, { backgroundColor: s.color }]} />
              <Text style={styles.legendLabel} numberOfLines={1}>{s.label}</Text>
            </View>
            <Text style={styles.legendValue}>{s.value}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.lg,
  },
  centerLabelWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerLabel: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  legend: {
    maxWidth: 160,
    gap: designTokens.spacing.xs,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendLabelGroup: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
  },
  swatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },
  legendLabel: {
    flexShrink: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  legendValue: {
    flexShrink: 0,
    minWidth: 24,
    marginLeft: designTokens.spacing.sm,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'right',
  },
});
